import { useMemo, useState } from 'react';
import { addDays, eachDayOfInterval, endOfWeek, format, parseISO, startOfWeek } from 'date-fns';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useCatalogs, useTasks } from '@/hooks/useTasks';
import { useAppStore } from '@/store/appStore';
import { applyFilters } from '@/components/filters/FilterPanel';
import type { Task } from '@/types';
import { contrastingText } from '@/lib/utils';

export function PlannerView() {
  const [anchor, setAnchor] = useState(new Date());
  const selectTask = useAppStore(s => s.selectTask);
  const filters = useAppStore(s => s.filters);
  const { data: catalogs } = useCatalogs();
  const { data: tasks = [] } = useTasks('project');

  const filteredTasks = useMemo(() => applyFilters(tasks, filters), [tasks, filters]);

  const start = startOfWeek(anchor, { weekStartsOn: 1 });
  const end = endOfWeek(anchor, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });

  const projectsList = useMemo(() => catalogs?.projects ?? [], [catalogs]);

  function tasksForProject(projectId: number): Task[] {
    return filteredTasks.filter(t => {
      if (t.idProyecto !== projectId) return false;
      if (!t.fechaInicio || !t.fechaProyectada) return false;
      const ts = parseISO(t.fechaInicio);
      const te = parseISO(t.fechaProyectada);
      return ts <= end && te >= start;
    });
  }

  function taskBarStyle(t: Task) {
    const ts = parseISO(t.fechaInicio!);
    const te = parseISO(t.fechaProyectada!);
    const visibleStart = ts < start ? start : ts;
    const visibleEnd = te > end ? end : te;
    const startIdx = days.findIndex(d => format(d, 'yyyy-MM-dd') === format(visibleStart, 'yyyy-MM-dd'));
    const endIdx = days.findIndex(d => format(d, 'yyyy-MM-dd') === format(visibleEnd, 'yyyy-MM-dd'));
    const left = (startIdx / days.length) * 100;
    const width = ((endIdx - startIdx + 1) / days.length) * 100;
    const bg = t.colorProyecto || '#888';
    return { left: `${left}%`, width: `${width}%`, background: bg, color: contrastingText(bg) };
  }

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center justify-between px-4 py-2 border-b border-stone-200">
        <div className="flex items-center gap-2">
          <button onClick={() => setAnchor(addDays(anchor, -7))} className="p-1 hover:bg-stone-100 rounded">
            <CaretLeft size={16} weight="bold" />
          </button>
          <span className="text-sm font-medium">
            {format(start, 'MMM dd')} – {format(end, 'MMM dd, yyyy')}
          </span>
          <button onClick={() => setAnchor(addDays(anchor, 7))} className="p-1 hover:bg-stone-100 rounded">
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-stone-50 sticky top-0">
              <th className="w-48 text-left px-3 py-2 font-medium text-stone-700 border-b border-stone-200">
                Nombre del recurso
              </th>
              {days.map(d => {
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <th
                    key={d.toISOString()}
                    className={`px-2 py-2 text-center font-normal text-xs border-b border-l border-stone-200 ${isWeekend ? 'bg-pink-50' : ''}`}
                  >
                    {format(d, 'eee d/M')}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {projectsList.map(p => {
              const projTasks = tasksForProject(p.idProyecto);
              return (
                <tr key={p.idProyecto} className="border-b border-stone-100">
                  <td className="px-3 py-3 text-stone-800">{p.nombre}</td>
                  <td colSpan={days.length} className="relative h-12 p-0">
                    <div className="absolute inset-0 flex">
                      {days.map((d, i) => {
                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                        return <div key={i} className={`flex-1 border-l border-stone-100 ${isWeekend ? 'bg-pink-50/40' : ''}`} />;
                      })}
                    </div>
                    {projTasks.map((t, idx) => (
                      <div
                        key={t.idTareaArq}
                        onClick={() => selectTask(t.idTareaArq)}
                        className="absolute h-7 rounded-full px-2 text-xs flex items-center cursor-pointer hover:opacity-90 truncate"
                        style={{ ...taskBarStyle(t), top: `${4 + (idx % 2) * 28}px` }}
                        title={t.nombreTarea ?? ''}
                      >
                        {t.nombreTarea}
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}